const MAX_ATTEMPTS = 5;
const DEFAULT_RETRY_SECONDS = 20;
const ORDER_ID_PATTERN = /^[0-9a-f]{32}$/i;

const normalizePaymentOrderId = (value) => (
    typeof value === 'string' && value.length === 32 && ORDER_ID_PATTERN.test(value)
        ? value.toLowerCase()
        : null
);

const derivePaymentView = ({ status, paid }) => {
    if (status === 'pending') return 'pending';
    if (status === 'canceled') return 'canceled';
    if (status === 'succeeded' && paid === true) return 'succeeded';
    return 'processing';
};

const getPaymentReturnAction = (isPublic) => isPublic === false
    ? { to: '/payment/', text: 'Вернуться в Личный кабинет' }
    : { to: '/pay/', text: 'Вернуться к форме оплаты' };

const createInitialState = () => ({
    view: 'loading',
    attempt: 0,
    secondsRemaining: DEFAULT_RETRY_SECONDS,
    status: null,
    paid: false,
    isPublic: null,
    message: '',
});

class PaymentStatusPoller {
    constructor({
        request,
        onChange = () => {},
        setTimeoutFn = setTimeout,
        clearTimeoutFn = clearTimeout,
        setIntervalFn = setInterval,
        clearIntervalFn = clearInterval,
    }) {
        this.request = request;
        this.onChange = onChange;
        this.setTimeoutFn = setTimeoutFn;
        this.clearTimeoutFn = clearTimeoutFn;
        this.setIntervalFn = setIntervalFn;
        this.clearIntervalFn = clearIntervalFn;
        this.timeoutHandle = null;
        this.intervalHandle = null;
        this.disposed = false;
        this.generation = 0;
        this.orderId = null;
        this.state = createInitialState();
    }

    async start(rawOrderId) {
        if (this.disposed) return;

        this.generation += 1;
        const generation = this.generation;
        this.clearTimers();
        this.orderId = normalizePaymentOrderId(rawOrderId);
        this.state = createInitialState();
        if (!this.orderId) {
            this.update({ view: 'error', message: 'Не удалось определить платёж' });
            return;
        }
        await this.attempt(generation, this.orderId);
    }

    isCurrent(generation, orderId) {
        return !this.disposed
            && this.generation === generation
            && this.orderId === orderId;
    }

    async attempt(generation, orderId) {
        if (!this.isCurrent(generation, orderId) || this.state.attempt >= MAX_ATTEMPTS) return;

        this.update({ view: 'loading', attempt: this.state.attempt + 1, message: '' });
        if (!this.isCurrent(generation, orderId)) return;

        let result;
        try {
            result = await this.request(orderId);
        } catch (error) {
            result = { error: '', status: null };
        }

        if (!this.isCurrent(generation, orderId)) return;

        const safeResult = result || { error: 'Ошибка отправки данных', status: null };
        const payload = safeResult.data && safeResult.data.data;
        if (!safeResult.error && payload) {
            if (typeof payload.is_public === 'boolean') {
                this.state.isPublic = payload.is_public;
            }
            const view = derivePaymentView(payload);
            this.update({ view, status: payload.status, paid: payload.paid });
            if (!this.isCurrent(generation, orderId)) return;
            if (view === 'pending' && this.state.attempt < MAX_ATTEMPTS) {
                this.schedule(DEFAULT_RETRY_SECONDS, generation, orderId);
            }
            return;
        }

        const retryable = safeResult.status == null
            || safeResult.status === 503
            || safeResult.status === 429;
        if (retryable && this.state.attempt < MAX_ATTEMPTS) {
            const seconds = Number.isInteger(safeResult.retryAfter) && safeResult.retryAfter > 0
                ? safeResult.retryAfter
                : DEFAULT_RETRY_SECONDS;
            this.update({
                view: 'retry_wait',
                message: safeResult.error || 'Временно не удалось проверить платёж',
            });
            if (!this.isCurrent(generation, orderId)) return;
            this.schedule(seconds, generation, orderId);
            return;
        }

        if (retryable) {
            this.update({
                view: 'error',
                message: 'Не удалось проверить статус платежа',
            });
            if (!this.isCurrent(generation, orderId)) return;
            return;
        }

        this.update({
            view: 'error',
            message: safeResult.error || 'Не удалось проверить статус платежа',
        });
        if (!this.isCurrent(generation, orderId)) return;
    }

    update(patch) {
        this.state = { ...this.state, ...patch };
        this.onChange({ ...this.state });
    }

    clearTimers() {
        if (this.timeoutHandle !== null) {
            this.clearTimeoutFn(this.timeoutHandle);
            this.timeoutHandle = null;
        }
        if (this.intervalHandle !== null) {
            this.clearIntervalFn(this.intervalHandle);
            this.intervalHandle = null;
        }
    }

    schedule(seconds, generation, orderId) {
        if (!this.isCurrent(generation, orderId)) return;
        this.clearTimers();
        if (!this.isCurrent(generation, orderId)) return;
        this.update({ secondsRemaining: seconds });
        if (!this.isCurrent(generation, orderId)) return;
        const intervalHandle = this.setIntervalFn(() => {
            if (!this.isCurrent(generation, orderId)) return;
            this.update({ secondsRemaining: Math.max(0, this.state.secondsRemaining - 1) });
        }, 1000);
        if (!this.isCurrent(generation, orderId)) {
            this.clearIntervalFn(intervalHandle);
            return;
        }
        this.intervalHandle = intervalHandle;
        const timeoutHandle = this.setTimeoutFn(async () => {
            if (!this.isCurrent(generation, orderId)) return;
            this.clearTimers();
            if (!this.isCurrent(generation, orderId)) return;
            await this.attempt(generation, orderId);
        }, seconds * 1000);
        if (!this.isCurrent(generation, orderId)) {
            this.clearTimeoutFn(timeoutHandle);
            return;
        }
        this.timeoutHandle = timeoutHandle;
    }

    dispose() {
        this.disposed = true;
        this.generation += 1;
        this.clearTimers();
    }
}

module.exports = {
    normalizePaymentOrderId,
    derivePaymentView,
    getPaymentReturnAction,
    PaymentStatusPoller,
};
